import { type Ref, ref, watch } from 'vue'
import type { InventoryMapObjectTypes } from '@/widgets'
import { isLine, isPoint, isPolygon, resolveFigureGeomType } from './helpers.ts'
import { DateTime } from 'ts-luxon'
import { distance, point } from '@turf/turf'
import { InventoryLineTypeData } from '@/entities/Inventory/Main'
import { pullAt } from 'lodash'
import { useInventLineCreateModal } from '@/widgets/InventoryLineCreateModal'
import { ObjectTypeFunctionalType } from '@/entities/Manuals/ObjectTypes'

export const useCreateInventObject = (
  pointerMapCoordinates: Ref<number[]>,
  addObjectType: Ref<InventoryMapObjectTypes | null>,
  addObject: Function,
) => {
  const isEditFigure = ref(false)
  const pointerCount = ref(0)
  const editFeature = ref<any | null>(null)

  const { show } = useInventLineCreateModal()

  /* Функция сброса значений */
  const resetValues = () => {
    isEditFigure.value = false
    pointerCount.value = 0
    editFeature.value = null
  }

  /**
   * Функция получения координат в зависимости от типа объекта
   */
  const getCoords = () => {
    return isPolygon(editFeature.value.geometry.type)
      ? [...editFeature.value.geometry.coordinates[0]]
      : [...editFeature.value.geometry.coordinates]
  }

  /**
   * Функция установки координат для редактируемого объекта
   * @param coords
   */
  const setCoords = (coords: number[]) => {
    editFeature.value = {
      ...editFeature.value,
      geometry: {
        ...editFeature.value.geometry,
        coordinates: isPolygon(editFeature.value.geometry.type) ? [coords] : coords,
      },
    }
  }

  /**
   * Функция инициализации/отмены рисования объекта
   */
  const initDrawHandler = () => {
    addObjectType.value ? initDraw() : resetValues()
  }

  /* Следим за выбранным типом объекта и обновляем редактируемый объект */
  watch(addObjectType, () => initDrawHandler())

  /* Функция инициализации отрисовки */
  const initDraw = () => {
    if (!addObjectType.value) return

    /* Сбрасываем значения при смене типа объекта */
    resetValues()

    const figure = addObjectType.value.figure
    const geomType = resolveFigureGeomType(figure)
    const style = addObjectType.value.styles
    const line = addObjectType.value.line

    if (isPoint(geomType)) return

    editFeature.value = {
      geometry: {
        type: geomType,
        coordinates: isLine(geomType) ? [] : [[]],
      },
      style: {
        stroke: [{ ...style.stroke, dash: InventoryLineTypeData[line.code] }],
        fill: style.fill,
      },
    }
  }

  /**
   * Функция отмены последней точки при рисовании
   */
  const cancelPointHandler = () => {
    if (pointerCount.value > 2) {
      const coords = getCoords()

      pullAt(coords, pointerCount.value - 1)
      pointerCount.value = pointerCount.value - 1

      setCoords(coords)
    } else {
      initDraw()
    }
  }

  /* Следим за изменением положения мыши и задаем координаты последней точки*/
  watch(pointerMapCoordinates, () => {
    if (isEditFigure.value && editFeature.value && pointerCount.value !== 0) {
      const coords = getCoords()

      coords[pointerCount.value] = pointerMapCoordinates.value

      setCoords(coords)
    }
  })

  /** Время и функция определения перетаскивания через миллисекунды */
  const time = ref(0)

  const drawDownHandler = () => {
    time.value = DateTime.now().toMillis()
  }

  /** Same 200ms threshold as drawHandler — distinguishes click from map pan. */
  const isQuickMapClick = () => DateTime.now().toMillis() - time.value <= 200

  /**
   * Функция рисования объекта
   * @param _
   * @param event
   */
  const drawHandler = async (_: any, event: any) => {
    /* Исключаем вероятность перемещения карты */
    if (!isQuickMapClick()) return

    /* Создание marker */
    if (addObjectType.value && isPoint(resolveFigureGeomType(addObjectType.value.figure))) {
      const currentCoordinate = event.coordinates

      addObject([currentCoordinate])
    }

    /* Создание feature */
    const drawGeomType = resolveFigureGeomType(addObjectType.value?.figure)
    if (addObjectType.value && !isPoint(drawGeomType)) {
      try {
        let currentCoordinate = event.coordinates

        /** Для линий определяем ближайшие точки */
        if (!pointerCount.value && isLine(drawGeomType)) {
          if (addObjectType.value.functional_type !== ObjectTypeFunctionalType.STANDARD) {
            const result = await show(event.coordinates)
            currentCoordinate = result.coords
          }
        }

        /* Отлавливаем окончание рисования фигуры */
        if (pointerCount.value > 1) {
          const editCoordinates = getCoords()

          const lastCoordinate = editCoordinates[editCoordinates.length - 2]
          const firstCoordinate = editCoordinates[0]

          const isCloseToLast = lastCoordinate && isPointClose(lastCoordinate, currentCoordinate)
          const isCloseToFirst = firstCoordinate && isPointClose(firstCoordinate, currentCoordinate)

          if (isCloseToLast || isCloseToFirst) {
            const coords = getCoords()
            const options: { [key: string]: any } = {}

            coords.pop()

            /** Для линий определяем ближайшие точки */
            if (isLine(editFeature.value.geometry.type)) {
              if (addObjectType.value.functional_type !== ObjectTypeFunctionalType.STANDARD) {
                const lastIndex = coords.length - 1
                const result = await show(coords[coords.length - 1])

                coords[lastIndex] = result.coords
                options['children'] = result.children
              }
            }

            if (isPolygon(editFeature.value.geometry.type)) {
              coords.push(coords[0])
            }

            addObject(coords, false, options)

            initDraw()
            return
          }
        }

        isEditFigure.value = true
        pointerCount.value = pointerCount.value + 1
        const coords = getCoords()

        setCoords([...coords, currentCoordinate])
      } catch (e) {}
    }
  }

  /**
   * Функция определения близости к точке
   * @param coordsFirst
   * @param coordsLast
   */
  const isPointClose = (coordsFirst: [number, number], coordsLast: [number, number]): boolean => {
    /* Расстояние чувствительности в метрах */
    const toleranceDistance = 0.001

    const point1 = point(coordsFirst)
    const point2 = point(coordsLast)

    const dd = distance(point1, point2)

    return dd <= toleranceDistance
  }

  return {
    isEditFigure,
    pointerCount,
    editFeature,

    drawHandler,
    drawDownHandler,
    isQuickMapClick,
    initDrawHandler,
    cancelPointHandler,
  }
}
